import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Select, { MultiValue } from 'react-select';
import Project from '../../common/Utils/project';
import { useAuth } from 'oidc-react';
import { CheckCircle } from 'lucide-react';
import api from '../services/apiService';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import ConfirmationModal from '../Modal/ConfirmationModal';
import PreviewDetails, { PreviewSection } from '../Modal/PreviewDetails';

type GroupOption = { label: string; value: string };

const groupSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    backgroundColor: '#1D1D1D',
    borderColor: state.isFocused ? '#DC2626' : '#374151',
    boxShadow: 'none',
    minHeight: 42,
    '&:hover': { borderColor: '#DC2626' },
  }),
  menu: (base: any) => ({ ...base, backgroundColor: '#1D1D1D' }),
  option: (base: any, { isFocused, isSelected }: any) => ({
    ...base,
    backgroundColor: isSelected ? '#DC2626' : isFocused ? '#2a2a2a' : 'transparent',
    color: '#fff',
    cursor: 'pointer',
  }),
  multiValue: (base: any) => ({ ...base, backgroundColor: '#2a2a2a', borderRadius: 6 }),
  multiValueLabel: (base: any) => ({ ...base, color: '#fff' }),
  multiValueRemove: (base: any) => ({
    ...base,
    color: '#f87171',
    ':hover': { backgroundColor: 'transparent', color: '#ef4444' },
  }),
  singleValue: (base: any) => ({ ...base, color: '#fff' }),
  input: (base: any) => ({ ...base, color: '#fff' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base: any) => ({ ...base, color: '#9CA3AF' }),
};

interface FormValues {
  loginEmail: string;
  customer: string;
  firstName: string;
  lastName: string;
  email: string;
  contactNum: string;
  roles: string[];
  activity: string;
}

interface TenantGroup {
  tenantName: string;
  tenantGroupID: string;
}

interface CustomerOption {
  tenantId: string;
  tenantName: string;
}

const initialValues: FormValues = {
  loginEmail: '',
  customer: '',
  firstName: '',
  lastName: '',
  email: '',
  contactNum: '',
  roles: [],
  activity: '',
};

const validationSchema = Yup.object({
  loginEmail: Yup.string().email('Invalid email').required('Required'),
  //customer: Yup.string().required('Required'),
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  //email: Yup.string().email('Invalid email').required('Required'),
  contactNum: Yup.string()
    .matches(/^[0-9]+$/, 'Must be only digits')
    .min(10)
    .required('Required'),
  //roles: Yup.array().min(1, 'Select at least one role'),
  //activity: Yup.string().required('Required'),
});

const GenerateLicenseForm: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const role = auth.userData?.profile?.role;
  const [customerList, setCustomerList] = useState<CustomerOption[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isSuccess, setIsSuccess] = useState(false);
  // Validated form values waiting for confirmation in the preview; nothing is created until then.
  const [previewValues, setPreviewValues] = useState<FormValues | null>(null);

  const [groupsApi, setGroupsApi] = useState<TenantGroup[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const { addToast } = useToast();

  const getSquareStyle = (isActive: boolean) => ({
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    border: '2px solid #444',
    backgroundColor: isActive ? '#00FF00' : 'transparent',
    cursor: 'pointer',
    display: 'inline-block',
  });

  const handleSubmit = (values: FormValues) => {
    if (values.roles.includes('Tenant Group Admin') && selectedGroupIds.length === 0) {
      addToast('Please choose at least one group for the Tenant Group Admin.', 'error', 4000);
      return;
    }

    setPreviewValues(values);
  };

  const createAccount = async (values: FormValues) => {
    setIsSubmitting(true);
    try {
      const payload = {
        loginEmail: values.loginEmail,
        customerName: selectedCustomer?.tenantName ?? '',
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.contactNum,
        roles: values.roles.map((role) => role.toLowerCase().replace(/ /g, '')),
        accountActivity: values.activity === 'True' ? 'Active' : 'Inactive',
        // Only meaningful — and only required by the API — for the Tenant Group Admin role.
        tenantGroupIds: values.roles.includes('Tenant Group Admin') ? selectedGroupIds : undefined,
      };

      const response = await api.post(`${Project.authApi}authservice/api/auth/create-tenant-user`, payload);

      if (response && response.data) {
        addToast('Account creation completed successfully!', 'success', 4000);
        setIsSuccess(true);

        // Wait 3 seconds, then navigate
        setTimeout(() => {
          navigate('/Accounts');
        }, 3000);
      }
    } catch (error) {
      console.error('Error creating account:', error);
    } finally {
      setIsSubmitting(false); // ✅ Always reset
      setPreviewValues(null);
    }
  };

  // Shows what createAccount sends, so an unset "Account Active" reads Inactive here too.
  const getPreviewSections = (values: FormValues): PreviewSection[] => [
    {
      title: 'Personal Information',
      rows: [
        { label: 'Customer Name', value: selectedCustomer?.tenantName },
        { label: 'First Name', value: values.firstName },
        { label: 'Last Name', value: values.lastName },
        { label: 'Email', value: values.loginEmail },
        { label: 'Contact Number', value: values.contactNum },
      ],
    },
    {
      title: 'Access',
      rows: [
        { label: 'Role', value: values.roles.join(', ') || 'No role selected' },
        ...(values.roles.includes('Tenant Group Admin')
          ? [
              {
                label: 'Groups',
                value: groupsApi
                  .filter((g) => selectedGroupIds.includes(g.tenantGroupID))
                  .map((g) => g.tenantName)
                  .join(', '),
              },
            ]
          : []),
        { label: 'Account Status', value: values.activity === 'True' ? 'Active' : 'Inactive' },
      ],
    },
  ];

  const getCustomerNames = async () => {
    try {
      if (auth && auth.userData) {
        const tenantId = auth?.userData?.profile.tenant_id;
        const response = await api.get(`${Project.authApi}authservice/api/auth/${tenantId}/get-customer-name-with-tenant-id`);
        const { data } = response;
        const customers: CustomerOption[] = Array.isArray(data.data) ? data.data : [];
        setCustomerList(customers);
        setSelectedCustomer(customers[0] ?? null); // default first value
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Fetch group options for the currently selected customer's own tenant — not the logged-in
  // caller's tenant, so a Reseller/Globaladmin picking a different "Customer Name" sees that
  // customer's own groups rather than their own.
  const getTenantGroups = async (tenantId: string) => {
    try {
      const { data } = await api.get(`${Project.api}tenantservice/TenantGroup/${tenantId}/groups`);

      const apiGroups: TenantGroup[] =
        data?.data?.[0]?.tenantGroupList?.map((g: any) => ({
          tenantName: g.groupName,
          tenantGroupID: g.tenantGroupId,
        })) ?? [];

      setGroupsApi(apiGroups);
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  };

  useEffect(() => {
    getCustomerNames();
  }, []);

  // Re-fetch groups whenever the selected customer changes, and drop any group selection made
  // for the previously selected customer's tenant — those ids don't belong to the new one.
  useEffect(() => {
    if (!selectedCustomer) {
      setGroupsApi([]);
      return;
    }
    setSelectedGroupIds([]);
    getTenantGroups(selectedCustomer.tenantId);
  }, [selectedCustomer?.tenantId]);

  let allRoles = ['Reseller', 'Tenant Admin', 'Tenant Group Admin', 'Visitor'];
  // list for roles other than global admin
  const filteredRoles = ['Tenant Admin', 'Tenant Group Admin', 'Visitor'];
  if (role !== 'globaladmin') {
    allRoles = filteredRoles;
  }

  return (
    <div className="bg-[#121212] text-white p-6 rounded-xl w-full mx-auto shadow-lg">
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ values, setFieldValue }) => (
          <Form className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold border-b border-gray-700 pb-2 mb-4">Create User</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block mb-1 text-sm">Customer Name</label>

                  {customerList.length > 0 ? (
                    <select
                      value={selectedCustomer?.tenantId ?? ''}
                      onChange={(e) => {
                        const next = customerList.find((c) => c.tenantId === e.target.value) ?? null;
                        setSelectedCustomer(next);
                      }}
                      className="w-full px-3 py-2 bg-[#1D1D1D] border border-gray-700 rounded-md focus:outline-none"
                    >
                      {customerList.map((customer) => (
                        <option key={customer.tenantId} value={customer.tenantId}>
                          {customer.tenantName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Field
                      type="text"
                      value=""
                      readOnly
                      className="w-full px-3 py-2 bg-[#1D1D1D] border border-gray-700 rounded-md focus:outline-none"
                    />
                  )}

                  <ErrorMessage name="customer" component="div" className="text-red-400 text-xs mt-1" />
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold border-b border-gray-700 pb-2 mb-4">Personal Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm mb-1">First Name*</label>
                  <Field name="firstName" className="w-full border border-zinc-700 p-2 rounded bg-[#161617]" />
                  <ErrorMessage name="firstName" component="div" className="text-red-500 text-xs mt-1" />
                </div>
                <div>
                  <label className="block text-sm mb-1">Last Name*</label>
                  <Field name="lastName" className="w-full border border-zinc-700 p-2 rounded bg-[#161617]" />
                  <ErrorMessage name="lastName" component="div" className="text-red-500 text-xs mt-1" />
                </div>
                <div>
                  <label className="block text-sm text-white mb-1">Email*</label>
                  <Field name="loginEmail" type="email" className="w-full border border-zinc-700 p-2 rounded bg-[#161617]" />
                  <ErrorMessage name="loginEmail" component="div" className="text-red-500 text-xs mt-1" />
                </div>
                <div>
                  <label className="block text-sm text-white mb-1">Contact Number*</label>
                  <Field name="contactNum" className="w-full border border-zinc-700 p-2 rounded bg-[#161617]" />
                  <ErrorMessage name="contactNum" component="div" className="text-red-500 text-xs mt-1" />
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-700">
              <h2 className="text-base font-semibold text-gray-100 pb-2 mb-4 border-b border-gray-700">Miscellaneous Settings</h2>

              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/2 md:pr-8 border-b md:border-b-0 border-gray-700">
                  <p className="text-sm font-medium mb-3">Standard Roles</p>
                  <div className="space-y-4">
                    {allRoles.map((role) => {
                      const isActive = values.roles.includes(role);
                      return (
                        <label key={role} className="flex items-center gap-3 text-sm cursor-pointer">
                          <div
                            style={getSquareStyle(isActive)}
                            onClick={() =>
                              setFieldValue(
                                'roles',
                                isActive ? [] : [role], // Select one role only
                              )
                            }
                          />
                          {role}
                        </label>
                      );
                    })}
                  </div>
                  <ErrorMessage name="roles" component="div" className="text-red-400 text-xs mt-2" />
                </div>

                <div className="md:w-1/2 md:pl-8 pt-6 md:pt-0">
                  <p className="text-sm font-medium mb-3">Account Active</p>
                  <div className="space-y-4">
                    {['True', 'False'].map((activity) => {
                      const isActive = values.activity === activity;
                      return (
                        <label key={activity} className="flex items-center gap-3 text-sm cursor-pointer">
                          <div style={getSquareStyle(isActive)} onClick={() => setFieldValue('activity', activity)} />
                          {activity}
                        </label>
                      );
                    })}
                  </div>
                  <ErrorMessage name="activity" component="div" className="text-red-400 text-xs mt-2" />
                </div>
              </div>
              {/* Group Selection (multi-select — a user may belong to more than one group) */}
              {values.roles.includes('Tenant Group Admin') && (
                <div className="mt-4">
                  <p className="text-sm font-medium mb-3">Choose group(s) *</p>
                  <Select<GroupOption, true>
                    isMulti
                    options={groupsApi.map((g) => ({ label: g.tenantName, value: g.tenantGroupID }))}
                    value={groupsApi
                      .filter((g) => selectedGroupIds.includes(g.tenantGroupID))
                      .map((g) => ({ label: g.tenantName, value: g.tenantGroupID }))}
                    onChange={(selected: MultiValue<GroupOption>) => setSelectedGroupIds(selected.map((opt) => opt.value))}
                    placeholder="Select group(s)..."
                    styles={groupSelectStyles}
                    className="w-full"
                  />
                </div>
              )}
            </div>

            {isSuccess && (
              <div className="flex items-center p-4 text-green-600 bg-green-100 rounded">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span>Account creation completed successfully!</span>
              </div>
            )}

            <div className="flex justify-start gap-4 pt-4">
              <button type="reset" className="px-6 py-2 border border-white rounded-full hover:bg-white hover:text-black transition">
                Clear
              </button>
              <button
                type="button"
                className="px-6 py-2 border border-white rounded-full hover:bg-white hover:text-black transition"
                onClick={() => navigate('/Accounts')}
              >
                Discard
              </button>
              <button
                type="submit"
                disabled={isSubmitting || isSuccess}
                className={`px-6 py-2 rounded-full transition shadow-md ${
                  isSubmitting || isSuccess ? 'bg-gray-500 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {isSubmitting ? 'Processing...' : 'Preview'}
              </button>
            </div>
          </Form>
        )}
      </Formik>

      <ConfirmationModal
        show={!!previewValues}
        title="Review new user"
        description="Check these details. The account is created only after you confirm."
        confirmLabel="Create Account"
        cancelLabel="Back to edit"
        loading={isSubmitting}
        onConfirm={() => previewValues && createAccount(previewValues)}
        onClose={() => setPreviewValues(null)}
      >
        {previewValues && <PreviewDetails sections={getPreviewSections(previewValues)} />}
      </ConfirmationModal>
    </div>
  );
};

export default GenerateLicenseForm;
